import React from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

interface RedemptionFiltersProps {
  dateRange: string;
  onDateRangeChange: (range: string) => void;
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  selectedDistrict: string;
  onDistrictChange: (district: string) => void;
}

const RedemptionFilters: React.FC<RedemptionFiltersProps> = ({
  dateRange,
  onDateRangeChange,
  selectedCategory,
  onCategoryChange,
  selectedDistrict,
  onDistrictChange,
}) => {
  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '1y', label: 'Last year' },
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'Attractions', label: 'Attractions' },
    { value: 'Restaurants & Cafés', label: 'Restaurants & Cafés' },
    { value: 'Shopping/Retail', label: 'Shopping/Retail' },
    { value: 'Beauty & Wellness', label: 'Beauty & Wellness' },
    { value: 'Activities & Experiences', label: 'Activities & Experiences' },
    { value: 'Entertainment & Nightlife', label: 'Entertainment & Nightlife' },
    { value: 'Transport & Mobility', label: 'Transport & Mobility' },
    { value: 'Local Services', label: 'Local Services' },
    { value: 'Kids & Family', label: 'Kids & Family' },
  ];

  const districtOptions = [
    { value: 'all', label: 'All Vienna Districts' },
    { value: 'innere-stadt', label: 'Innere Stadt (1st)' },
    { value: 'leopoldstadt', label: 'Leopoldstadt (2nd)' },
    { value: 'landstrasse', label: 'Landstraße (3rd)' },
    { value: 'wieden', label: 'Wieden (4th)' },
    { value: 'mariahilf', label: 'Mariahilf (6th)' },
    { value: 'neubau', label: 'Neubau (7th)' },
    { value: 'josefstadt', label: 'Josefstadt (8th)' },
    { value: 'alsergrund', label: 'Alsergrund (9th)' },
    { value: 'favoriten', label: 'Favoriten (10th)' },
  ];

  const clearAllFilters = () => {
    onDateRangeChange('30d');
    onCategoryChange('all');
    onDistrictChange('all');
  };

  const hasActiveFilters = selectedCategory !== 'all' || selectedDistrict !== 'all';

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Filter Analytics
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Customize data views and time periods for detailed analysis
          </p>
        </div>

        {hasActiveFilters && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearAllFilters}
            className="lg:ml-4"
          >
            <Icon name="X" size={14} className="mr-2" />
            Clear Filters
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {/* Date Range Filter */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Time Period
          </label>
          <Select
            value={dateRange}
            onValueChange={onDateRangeChange}
          >
            {dateRangeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Category Filter */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Category
          </label>
          <Select
            value={selectedCategory}
            onValueChange={onCategoryChange}
          >
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/* District Filter */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Vienna District
          </label>
          <Select
            value={selectedDistrict}
            onValueChange={onDistrictChange}
          >
            {districtOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            
            {selectedCategory !== 'all' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                <Icon name="Tag" size={12} className="mr-1" />
                {categoryOptions.find(opt => opt.value === selectedCategory)?.label}
                <button 
                  onClick={() => onCategoryChange('all')}
                  className="ml-2 hover:text-primary/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            {selectedDistrict !== 'all' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                <Icon name="MapPin" size={12} className="mr-1" />
                {districtOptions.find(opt => opt.value === selectedDistrict)?.label}
                <button 
                  onClick={() => onDistrictChange('all')}
                  className="ml-2 hover:text-primary/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Quick Filter Buttons */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground mr-2">Quick filters:</span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onDateRangeChange('7d');
              onCategoryChange('Restaurants & Cafés');
            }}
          >
            <Icon name="Coffee" size={12} className="mr-1" />
            Food (7 days)
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onDateRangeChange('30d');
              onCategoryChange('Attractions');
            }}
          >
            <Icon name="MapPin" size={12} className="mr-1" />
            Tourism (30 days)
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onDateRangeChange('30d');
              onDistrictChange('innere-stadt');
            }}
          >
            <Icon name="Building" size={12} className="mr-1" />
            City Center
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RedemptionFilters;